import React, {
  Suspense,
  lazy,
  useMemo,
  useRef,
  useState,
} from "react";
import { Trans } from "react-i18next";
import type { EEGChannel } from "../services/EEGWaveformRenderer";
import {
  LIVE_SCALP_DOCK_MAP_HEIGHT,
  LIVE_SCALP_DOCK_MAP_WIDTH,
  formatLiveTopographyBufferSemantics,
  formatLiveTopographyScaleSemantics,
  formatLiveTopographyScaleValue,
  formatLiveTopographyTimeSemantics,
  type LiveTopographyPreviewModel,
} from "./eegViewerLiveTopography";
import {
  LIVE_SCALP_BAND_KEYS,
  LIVE_SCALP_IFU,
  buildLiveScalpPropagationFrames,
  buildLiveTopographyAveragePreview,
  buildLiveTopographyBandPowerPreview,
  buildLiveTopographyEventAveragePreview,
  buildLiveTopographyPreviewFromChannels,
  findLiveScalpMaxFocus,
  findLiveScalpPeakTimeSeconds,
  formatLiveScalpMaxFocus,
  formatLiveScalpMontageLabel,
  type LiveScalpBandKey,
  type LiveScalpLocalizeField,
  type LiveScalpViewMode,
} from "./liveScalpLocalizeHelpers";
import {
  buildLiveScalpExportFilename,
  buildLiveScalpExportMetadata,
  canvasToPngBlob,
  compositeLiveScalpExportCanvas,
  copyPngBlobToClipboard,
  copyTextToClipboard,
  downloadBlob,
} from "./liveScalpExportHelpers";
import { EegDockPanelChrome } from "./EegDockPanelChrome";
import { EegDockPanelLabeling } from "./EegDockPanelLabeling";
import { SOURCE_LOCALIZATION_PRODUCTION_ENABLED } from "../utils/sourceLocalizationClinician";
import type { LiveScalpInterpolatedMapHandle } from "./LiveScalpInterpolatedMap";
import type { DetectedSpike } from "../types/detectionTypes";
import type { AnnotationListItem } from "./AnnotationListView";

export type LiveScalpAnnotationLike = {
  annotation_type: string;
  label?: string | null;
  start_time: number;
  end_time?: number | null;
};

const EEGProjectedTopographyPreview = lazy(
  () => import("./EEGProjectedTopographyPreview"),
);
const LiveScalpInterpolatedMap = lazy(
  () => import("./LiveScalpInterpolatedMap"),
);

const LOCALIZE_MAP_WIDTH = 280;
const LOCALIZE_MAP_HEIGHT = 200;
const PROP_THUMB_WIDTH = 56;
const PROP_THUMB_HEIGHT = 40;

export type LiveScalpFieldEventHint = {
  startSeconds: number;
  endSeconds: number;
  label: string;
  source?: "triage" | "catalog" | "spike" | "manual";
};

export type LiveScalpDockPanelProps = {
  preview: LiveTopographyPreviewModel;
  channelSeries?: EEGChannel[];
  currentTimeSeconds?: number;
  timeScaleSeconds?: number;
  montageName?: string | null;
  annotations?: LiveScalpAnnotationLike[];
  annotationListItems?: AnnotationListItem[];
  spikes?: DetectedSpike[];
  fieldEventHint?: LiveScalpFieldEventHint | null;
  onSeek?: (timeSeconds: number) => void;
  onElectrodeFocus?: (label: string) => void;
  onClearFieldEventHint?: () => void;
};

function collectEventTimesSeconds(
  annotations: LiveScalpAnnotationLike[] | undefined,
  annotationListItems: AnnotationListItem[] | undefined,
  spikes: DetectedSpike[] | undefined,
  cursorSeconds: number,
  halfWindowSeconds: number,
): number[] {
  const windowStart = cursorSeconds - halfWindowSeconds;
  const windowEnd = cursorSeconds + halfWindowSeconds;
  const times: number[] = [];

  for (const annotation of annotations ?? []) {
    const type = (
      annotation.annotation_type ||
      annotation.label ||
      ""
    ).toLowerCase();
    if (
      type.includes("spike") ||
      type.includes("ied") ||
      type.includes("epileptiform")
    ) {
      if (
        Number.isFinite(annotation.start_time) &&
        annotation.start_time >= windowStart - 30 &&
        annotation.start_time <= windowEnd + 30
      ) {
        times.push(annotation.start_time);
      }
    }
  }

  for (const item of annotationListItems ?? []) {
    const type = (item.typeCode || item.label || "").toLowerCase();
    if (
      type.includes("spike") ||
      type.includes("ied") ||
      type.includes("epileptiform")
    ) {
      if (
        Number.isFinite(item.startTime) &&
        item.startTime >= windowStart - 30 &&
        item.startTime <= windowEnd + 30
      ) {
        times.push(item.startTime);
      }
    }
  }

  for (const spike of spikes ?? []) {
    if (
      Number.isFinite(spike.time) &&
      spike.time >= windowStart - 30 &&
      spike.time <= windowEnd + 30
    ) {
      times.push(spike.time);
    }
  }

  return Array.from(new Set(times.map((t) => Number(t.toFixed(3))))).sort(
    (a, b) => a - b,
  );
}

/**
 * Live scalp topography as a compact right-rail adjunct (pop-out capable).
 * Localize (S0–S4) adds IFU, interpolated field, max focus, montage identity,
 * spike/event maps, map→trace, propagation, band-power maps, and export.
 */
export default function LiveScalpDockPanel({
  preview,
  channelSeries = [],
  currentTimeSeconds,
  timeScaleSeconds = 10,
  montageName = null,
  annotations = [],
  annotationListItems = [],
  spikes = [],
  fieldEventHint = null,
  onSeek,
  onElectrodeFocus,
  onClearFieldEventHint,
}: LiveScalpDockPanelProps) {
  const [viewMode, setViewMode] = useState<LiveScalpViewMode>("live");
  const [localizeField, setLocalizeField] =
    useState<LiveScalpLocalizeField>("cursor");
  const [bandKey, setBandKey] = useState<LiveScalpBandKey>("alpha");
  const [propagationIndex, setPropagationIndex] = useState(0);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const mapRef = useRef<LiveScalpInterpolatedMapHandle | null>(null);

  const cursorTime = currentTimeSeconds ?? preview.sampleTimeSeconds ?? 0;
  const bufferStart = preview.bufferStartSeconds;
  const bufferEnd = preview.bufferEndSeconds;
  const channelsReady =
    channelSeries.length > 0 &&
    bufferStart != null &&
    Number.isFinite(bufferStart);

  const pageHalf = Math.max(0.25, (timeScaleSeconds || 10) / 2);
  const windowStart = Math.max(0, cursorTime - pageHalf);
  const windowEnd = cursorTime + pageHalf;

  const eventTimes = useMemo(() => {
    const collected = collectEventTimesSeconds(
      annotations,
      annotationListItems,
      spikes,
      cursorTime,
      Math.max(pageHalf, 15),
    );
    if (fieldEventHint) {
      const mid =
        (fieldEventHint.startSeconds + fieldEventHint.endSeconds) / 2;
      return Array.from(new Set([...collected, Number(mid.toFixed(3))])).sort(
        (a, b) => a - b,
      );
    }
    return collected;
  }, [
    annotations,
    annotationListItems,
    spikes,
    cursorTime,
    pageHalf,
    fieldEventHint,
  ]);

  const localizeWindowStart = fieldEventHint
    ? fieldEventHint.startSeconds
    : windowStart;
  const localizeWindowEnd = fieldEventHint
    ? Math.max(fieldEventHint.endSeconds, fieldEventHint.startSeconds + 0.04)
    : windowEnd;

  const applyEventHintToLocalize = () => {
    if (!fieldEventHint) {
      return;
    }
    setViewMode("localize");
    setLocalizeField("average");
    setPropagationIndex(0);
    onSeek?.(fieldEventHint.startSeconds);
  };

  const localizePreview = useMemo((): LiveTopographyPreviewModel => {
    if (viewMode !== "localize" || !channelsReady || bufferStart == null) {
      return preview;
    }

    if (localizeField === "cursor") {
      return buildLiveTopographyPreviewFromChannels({
        channels: channelSeries,
        sampleTimeSeconds: cursorTime,
        bufferStartSeconds: bufferStart,
        bufferEndSeconds: bufferEnd,
        statusPrefix: "Localize",
      });
    }

    if (localizeField === "peak") {
      const peakTime = findLiveScalpPeakTimeSeconds(
        channelSeries,
        bufferStart,
        localizeWindowStart,
        localizeWindowEnd,
        0.02,
      );
      if (peakTime == null) {
        return preview;
      }
      return buildLiveTopographyPreviewFromChannels({
        channels: channelSeries,
        sampleTimeSeconds: peakTime,
        bufferStartSeconds: bufferStart,
        bufferEndSeconds: bufferEnd,
        statusPrefix: "Peak",
      });
    }

    if (localizeField === "average") {
      if (eventTimes.length > 0) {
        return buildLiveTopographyEventAveragePreview({
          channels: channelSeries,
          bufferStartSeconds: bufferStart,
          bufferEndSeconds: bufferEnd,
          eventTimesSeconds: eventTimes.slice(0, 40),
        });
      }
      return buildLiveTopographyAveragePreview({
        channels: channelSeries,
        bufferStartSeconds: bufferStart,
        bufferEndSeconds: bufferEnd,
        windowStartSeconds: localizeWindowStart,
        windowEndSeconds: localizeWindowEnd,
        stepSeconds: 0.02,
      });
    }

    if (localizeField === "band") {
      return buildLiveTopographyBandPowerPreview({
        channels: channelSeries,
        bufferStartSeconds: bufferStart,
        bufferEndSeconds: bufferEnd,
        windowStartSeconds: localizeWindowStart,
        windowEndSeconds: localizeWindowEnd,
        band: bandKey,
      });
    }

    return preview;
  }, [
    bandKey,
    bufferEnd,
    bufferStart,
    channelSeries,
    channelsReady,
    cursorTime,
    eventTimes,
    localizeField,
    localizeWindowEnd,
    localizeWindowStart,
    preview,
    viewMode,
  ]);

  const propagationFrames = useMemo(() => {
    if (
      viewMode !== "localize" ||
      localizeField !== "propagation" ||
      !channelsReady ||
      bufferStart == null
    ) {
      return [];
    }
    return buildLiveScalpPropagationFrames({
      channels: channelSeries,
      bufferStartSeconds: bufferStart,
      bufferEndSeconds: bufferEnd,
      windowStartSeconds: localizeWindowStart,
      windowEndSeconds: localizeWindowEnd,
      frameCount: 8,
    });
  }, [
    bufferEnd,
    bufferStart,
    channelSeries,
    channelsReady,
    localizeField,
    localizeWindowEnd,
    localizeWindowStart,
    viewMode,
  ]);

  const activePreview =
    viewMode === "localize" && localizeField === "propagation"
      ? (propagationFrames[
          Math.min(
            propagationIndex,
            Math.max(propagationFrames.length - 1, 0),
          )
        ]?.preview ?? preview)
      : viewMode === "localize"
        ? localizePreview
        : preview;

  const maxFocus = findLiveScalpMaxFocus(activePreview.points);
  const maxFocusLabel = formatLiveScalpMaxFocus(maxFocus);
  const montageLabel = formatLiveScalpMontageLabel(
    montageName,
    channelSeries.map((channel) => channel.name),
  );
  const timeSemantics =
    viewMode === "live"
      ? formatLiveTopographyTimeSemantics(preview)
      : activePreview.status;
  const scaleSemantics = formatLiveTopographyScaleSemantics(activePreview);
  const bufferSemantics = formatLiveTopographyBufferSemantics(activePreview);
  const showMap = activePreview.points.length > 0;
  const midScale = (activePreview.minValue + activePreview.maxValue) / 2;
  const mapWidth =
    viewMode === "localize" ? LOCALIZE_MAP_WIDTH : LIVE_SCALP_DOCK_MAP_WIDTH;
  const mapHeight =
    viewMode === "localize" ? LOCALIZE_MAP_HEIGHT : LIVE_SCALP_DOCK_MAP_HEIGHT;

  const handleElectrodeClick = (label: string) => {
    onElectrodeFocus?.(label);
  };

  const handlePropagationSelect = (index: number, timeSeconds: number) => {
    setPropagationIndex(index);
    onSeek?.(timeSeconds);
  };

  const runExport = async (mode: "png" | "clipboard-text" | "clipboard-png") => {
    const metadata = buildLiveScalpExportMetadata({
      preview: activePreview,
      viewMode,
      localizeField,
      bandKey,
      montageLabel,
      maxFocus,
      eventHintLabel: fieldEventHint?.label ?? null,
    });

    if (mode === "clipboard-text") {
      const ok = await copyTextToClipboard(metadata.plaintext);
      setExportStatus(ok ? "Metadata copied" : "Clipboard unavailable");
      return;
    }

    const sourceCanvas = mapRef.current?.getCanvas();
    if (!sourceCanvas) {
      setExportStatus("Open Localize interpolated map to export PNG");
      return;
    }

    const composite = compositeLiveScalpExportCanvas(sourceCanvas, metadata);
    const blob = await canvasToPngBlob(composite);
    if (!blob) {
      setExportStatus("PNG export failed");
      return;
    }

    if (mode === "png") {
      downloadBlob(blob, buildLiveScalpExportFilename(metadata));
      setExportStatus(`Downloaded ${buildLiveScalpExportFilename(metadata)}`);
      return;
    }

    const copied = await copyPngBlobToClipboard(blob);
    if (copied) {
      setExportStatus("PNG copied to clipboard");
      return;
    }
    downloadBlob(blob, buildLiveScalpExportFilename(metadata));
    setExportStatus("Clipboard PNG unsupported — downloaded instead");
  };

  return (
    <EegDockPanelChrome panelKey="live_scalp">
      <section
        className={
          viewMode === "localize"
            ? "eeg-live-topography-card eeg-live-topography-card--dock eeg-live-topography-card--localize"
            : "eeg-live-topography-card eeg-live-topography-card--dock eeg-live-topography-card--compact"
        }
        aria-label="Live scalp preview"
        data-testid="live-scalp-dock-panel"
        data-live-scalp-mode={viewMode}
      >
        <div className="eeg-live-topography-card__header">
          <div className="eeg-live-topography-card__copy">
            <strong className="eeg-live-topography-card__title">
              <Trans ns="features" i18nKey="ui.copy.2bd1c8d2ae10" />
            </strong>
            <span className="eeg-live-topography-card__summary">
              {timeSemantics}
            </span>
          </div>
          <div
            className="eeg-live-topography-card__mode-toggle"
            role="group"
            aria-label="Live Scalp view mode"
          >
            <button
              type="button"
              className={
                viewMode === "live"
                  ? "eeg-live-topography-card__mode-btn is-active"
                  : "eeg-live-topography-card__mode-btn"
              }
              data-testid="live-scalp-mode-live"
              onClick={() => setViewMode("live")}
            >
              Live
            </button>
            <button
              type="button"
              className={
                viewMode === "localize"
                  ? "eeg-live-topography-card__mode-btn is-active"
                  : "eeg-live-topography-card__mode-btn"
              }
              data-testid="live-scalp-mode-localize"
              onClick={() => setViewMode("localize")}
            >
              Localize
            </button>
          </div>
        </div>

        {fieldEventHint && viewMode === "live" ? (
          <div
            className="eeg-live-topography-card__focus"
            data-testid="live-scalp-event-hint-offer"
          >
            <span>
              Event available: {fieldEventHint.label} (
              {fieldEventHint.startSeconds.toFixed(2)}–
              {fieldEventHint.endSeconds.toFixed(2)} s)
            </span>
            <button
              type="button"
              className="eeg-live-topography-card__focus-btn"
              data-testid="live-scalp-apply-event"
              onClick={applyEventHintToLocalize}
            >
              Apply in Localize
            </button>
            {onClearFieldEventHint ? (
              <button
                type="button"
                className="eeg-live-topography-card__focus-btn"
                data-testid="live-scalp-clear-event"
                onClick={onClearFieldEventHint}
              >
                Clear
              </button>
            ) : null}
          </div>
        ) : null}

        {viewMode === "localize" ? (
          <>
            <EegDockPanelLabeling
              data-testid="live-scalp-labeling"
              badge={LIVE_SCALP_IFU.badge}
              summary={LIVE_SCALP_IFU.summary}
              intendedUse={LIVE_SCALP_IFU.intendedUse}
              excludedUse={LIVE_SCALP_IFU.excludedUse}
              sessionNotes={
                <span data-testid="live-scalp-montage-label">
                  {montageLabel}
                </span>
              }
            />
            <div
              className="eeg-live-topography-card__field-row"
              role="group"
              aria-label="Localize field type"
            >
              {(
                [
                  ["cursor", "Cursor"],
                  ["peak", "Peak"],
                  ["average", "Average"],
                  ["band", "Band"],
                  ["propagation", "Prop"],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={
                    localizeField === id
                      ? "eeg-live-topography-card__field-btn is-active"
                      : "eeg-live-topography-card__field-btn"
                  }
                  data-testid={`live-scalp-field-${id}`}
                  onClick={() => {
                    setLocalizeField(id);
                    setPropagationIndex(0);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {localizeField === "band" ? (
              <div
                className="eeg-live-topography-card__field-row"
                role="group"
                aria-label="Band selection"
              >
                {LIVE_SCALP_BAND_KEYS.map((band) => (
                  <button
                    key={band}
                    type="button"
                    className={
                      bandKey === band
                        ? "eeg-live-topography-card__field-btn is-active"
                        : "eeg-live-topography-card__field-btn"
                    }
                    data-testid={`live-scalp-band-${band}`}
                    onClick={() => setBandKey(band)}
                  >
                    {band}
                  </button>
                ))}
              </div>
            ) : null}
            {maxFocusLabel ? (
              <div
                className="eeg-live-topography-card__focus"
                data-testid="live-scalp-max-focus"
              >
                <span>{maxFocusLabel}</span>
                {maxFocus && onElectrodeFocus ? (
                  <button
                    type="button"
                    className="eeg-live-topography-card__focus-btn"
                    data-testid="live-scalp-focus-channel"
                    onClick={() => onElectrodeFocus(maxFocus.label)}
                  >
                    Lock channel
                  </button>
                ) : null}
              </div>
            ) : null}
            {localizeField === "average" ? (
              <div
                className="eeg-live-topography-card__focus"
                data-testid="live-scalp-event-provenance"
              >
                {fieldEventHint
                  ? `Event field · ${fieldEventHint.label} (${fieldEventHint.source ?? "hint"}) · N=${Math.min(eventTimes.length, 40)}`
                  : eventTimes.length > 0
                    ? `Event average · N=${Math.min(eventTimes.length, 40)} near cursor (spike/IED annotations or detectors)`
                    : "Page-window average (no spike/IED events nearby)"}
              </div>
            ) : null}
            {fieldEventHint ? (
              <div
                className="eeg-live-topography-card__focus"
                data-testid="live-scalp-event-hint"
              >
                <span>
                  Locked event: {fieldEventHint.label} (
                  {fieldEventHint.startSeconds.toFixed(2)}–
                  {fieldEventHint.endSeconds.toFixed(2)} s)
                </span>
                {onClearFieldEventHint ? (
                  <button
                    type="button"
                    className="eeg-live-topography-card__focus-btn"
                    data-testid="live-scalp-clear-event"
                    onClick={onClearFieldEventHint}
                  >
                    Clear event
                  </button>
                ) : null}
              </div>
            ) : null}
            <div
              className="eeg-live-topography-card__field-row"
              role="group"
              aria-label="Live Scalp export"
            >
              <button
                type="button"
                className="eeg-live-topography-card__field-btn"
                data-testid="live-scalp-export-png"
                onClick={() => void runExport("png")}
              >
                Download PNG
              </button>
              <button
                type="button"
                className="eeg-live-topography-card__field-btn"
                data-testid="live-scalp-copy-metadata"
                onClick={() => void runExport("clipboard-text")}
              >
                Copy metadata
              </button>
              <button
                type="button"
                className="eeg-live-topography-card__field-btn"
                data-testid="live-scalp-copy-png"
                onClick={() => void runExport("clipboard-png")}
              >
                Copy PNG
              </button>
              <a
                className="eeg-live-topography-card__field-btn"
                data-testid="live-scalp-esi-link"
                href="/epilepsy/source-localization"
              >
                {SOURCE_LOCALIZATION_PRODUCTION_ENABLED
                  ? "ESI (assistive)"
                  : "ESI (deferred)"}
              </a>
            </div>
            {exportStatus ? (
              <div
                className="eeg-live-topography-card__focus"
                data-testid="live-scalp-export-status"
              >
                {exportStatus}
              </div>
            ) : null}
          </>
        ) : null}

        {!showMap ? (
          <div className="eeg-live-topography-card__empty">
            {activePreview.detail}
          </div>
        ) : (
          <>
            <Suspense
              fallback={
                <div className="eeg-live-topography-card__empty">
                  <Trans ns="features" i18nKey="ui.copy.d7c22d053c0a" />
                </div>
              }
            >
              {viewMode === "localize" && activePreview.points.length >= 3 ? (
                <LiveScalpInterpolatedMap
                  ref={mapRef}
                  width={mapWidth}
                  height={mapHeight}
                  points={activePreview.points}
                  minValue={activePreview.minValue}
                  maxValue={activePreview.maxValue}
                  focusLabel={maxFocus?.label ?? null}
                  onElectrodeClick={handleElectrodeClick}
                />
              ) : (
                <EEGProjectedTopographyPreview
                  width={mapWidth}
                  height={mapHeight}
                  points={activePreview.points}
                  minValue={activePreview.minValue}
                  maxValue={activePreview.maxValue}
                  showMeta={false}
                  onPointClick={handleElectrodeClick}
                />
              )}
            </Suspense>
            <div
              className="eeg-live-topography-card__scale-bar"
              role="img"
              aria-label={`Scale ${scaleSemantics}`}
            >
              <div className="eeg-live-topography-card__scale-bar-gradient" />
              <div className="eeg-live-topography-card__scale-bar-ticks">
                <span>
                  {formatLiveTopographyScaleValue(activePreview.minValue)}
                </span>
                <span>{formatLiveTopographyScaleValue(midScale)}</span>
                <span>
                  {formatLiveTopographyScaleValue(activePreview.maxValue)}{" "}
                  {activePreview.valueUnit}
                </span>
              </div>
            </div>
          </>
        )}

        {viewMode === "localize" &&
        localizeField === "propagation" &&
        propagationFrames.length > 0 ? (
          <div
            className="eeg-live-topography-card__propagation"
            data-testid="live-scalp-propagation-strip"
            role="list"
            aria-label="Propagation frames"
          >
            {propagationFrames.map((frame, index) => (
              <button
                key={`${frame.timeSeconds}-${index}`}
                type="button"
                role="listitem"
                className={
                  index === propagationIndex
                    ? "eeg-live-topography-card__prop-thumb is-active"
                    : "eeg-live-topography-card__prop-thumb"
                }
                data-testid={`live-scalp-prop-frame-${index}`}
                onClick={() =>
                  handlePropagationSelect(index, frame.timeSeconds)
                }
                title={`Propagate @ ${frame.timeSeconds.toFixed(2)} s`}
              >
                <Suspense fallback={null}>
                  <LiveScalpInterpolatedMap
                    width={PROP_THUMB_WIDTH}
                    height={PROP_THUMB_HEIGHT}
                    points={frame.preview.points}
                    minValue={frame.preview.minValue}
                    maxValue={frame.preview.maxValue}
                    resolution={24}
                    data-testid={`live-scalp-prop-canvas-${index}`}
                  />
                </Suspense>
                <span>{frame.timeSeconds.toFixed(1)}s</span>
              </button>
            ))}
          </div>
        ) : null}

        {bufferSemantics ? (
          <div className="eeg-live-topography-card__foot">{bufferSemantics}</div>
        ) : null}
      </section>
    </EegDockPanelChrome>
  );
}
